import { useEffect, useRef, useState } from "react";
import { Icon, Spinner, Text } from "@ttc3k/trekker";
import { Check, Xmark } from "iconoir-react";
import { Box, Center, Flex, styled } from "styled-system/jsx";
import { scrollToStep } from "widgets/booker/shared";
import type { CheckoutRequirement } from "./useCheckoutRequirements";

const ROW_STAGGER_MS = 90;
const ICON_DELAY_MS = 120;

const CHECKLIST_KEYFRAMES = `
@keyframes wa-checklist-row-in { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes wa-checklist-icon-pop { 0% { transform: scale(0); } 70% { transform: scale(1.15); } 100% { transform: scale(1); } }
@media (prefers-reduced-motion: reduce) { .wa-checklist-anim { animation: none !important; opacity: 1 !important; transform: none !important; } }
`;

const RowButton = styled("button", {
  base: {
    display: "flex",
    alignItems: "flex-start",
    gap: "100",
    w: "full",
    p: "0",
    bg: "transparent",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    borderRadius: "100",
    "&:hover .wa-checklist-label": { textDecoration: "underline" },
    _focusVisible: {
      outline: "2px solid",
      outlineColor: "border.dark",
      outlineOffset: "2px",
    },
  },
});

type CheckoutChecklistProps = {
  items: CheckoutRequirement[];
  heading: string;
  /** Shown instead of `heading` once every row is complete. */
  readyHeading: string;
};

export const CheckoutChecklist = ({
  items,
  heading,
  readyHeading,
}: CheckoutChecklistProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);
  // Rows and icons stagger in on the first reveal only; later changes animate
  // straight away.
  const [introDone, setIntroDone] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || revealed) return;
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [revealed]);

  const introLength = items.length;
  useEffect(() => {
    if (!revealed || introDone) return;
    const timer = setTimeout(
      () => setIntroDone(true),
      introLength * ROW_STAGGER_MS + ICON_DELAY_MS + 400,
    );
    return () => clearTimeout(timer);
  }, [revealed, introDone, introLength]);

  if (items.length === 0) return null;

  const allComplete = items.every(item => item.status === "complete");

  return (
    <Box
      ref={containerRef}
      w="full"
      p="200"
      borderRadius="100"
      border="1px solid"
      borderColor="border.light"
      bg="white"
    >
      <style>{CHECKLIST_KEYFRAMES}</style>
      <Text visual="smallSemiBold" color="text.dark" mb="150">
        {allComplete ? readyHeading : heading}
      </Text>
      <styled.ul
        display="flex"
        flexDirection="column"
        gap="100"
        listStyle="none"
        m="0"
        p="0"
      >
        {items.map((item, index) => (
          <ChecklistRow
            key={item.key}
            item={item}
            revealed={revealed}
            delayMs={introDone ? 0 : index * ROW_STAGGER_MS}
          />
        ))}
      </styled.ul>
    </Box>
  );
};

type ChecklistRowProps = {
  item: CheckoutRequirement;
  revealed: boolean;
  delayMs: number;
};

const ChecklistRow = ({ item, revealed, delayMs }: ChecklistRowProps) => {
  const clickable =
    (item.status === "incomplete" || item.status === "error") &&
    item.stepIds.length > 0;

  const content = (
    <>
      <Center
        // Re-mount on status change so the pop replays.
        key={item.status}
        className="wa-checklist-anim"
        flexShrink={0}
        w="20px"
        h="20px"
        style={
          revealed
            ? {
                animation: `wa-checklist-icon-pop 360ms ease-out ${delayMs + ICON_DELAY_MS}ms both`,
              }
            : { transform: "scale(0)" }
        }
      >
        <StatusIcon status={item.status} />
      </Center>
      <Flex flexDirection="column" gap="25" minW="0">
        <Text
          className="wa-checklist-label"
          visual="smallMedium"
          color={item.status === "complete" ? "text.mid" : "text.dark"}
        >
          {item.label}
        </Text>
        {item.message && (
          <Text visual="smallRegular" color="text.error.mid">
            {item.message}
          </Text>
        )}
      </Flex>
    </>
  );

  return (
    <styled.li
      className="wa-checklist-anim"
      style={
        revealed
          ? {
              animation: `wa-checklist-row-in 320ms ease-out ${delayMs}ms both`,
            }
          : { opacity: 0 }
      }
    >
      {clickable ? (
        <RowButton type="button" onClick={() => scrollToStep(item.stepIds)}>
          {content}
        </RowButton>
      ) : (
        <Flex alignItems="flex-start" gap="100">
          {content}
        </Flex>
      )}
    </styled.li>
  );
};

const StatusIcon = ({ status }: { status: CheckoutRequirement["status"] }) => {
  if (status === "pending") return <Spinner w="14px" h="14px" />;
  if (status === "complete") {
    return <Icon Element={Check} color="icon.success.mid" size="sm" />;
  }
  return <Icon Element={Xmark} color="icon.error.mid" size="sm" />;
};
